/**
 * useAutoReadMentions — opening the thing you were tagged in marks the tag read.
 *
 * The old system only cleared a mention when you explicitly pressed "Mark
 * read", so clicking through to a fault, reading the note, and closing the
 * modal left the notification sitting there unread forever. This hook is the
 * receiving end of the cascade: any surface that renders a mentionable entity
 * drops it in, and every unread mention pointing at that entity flips to READ
 * (and drops into the "Earlier" pile) the moment the surface opens.
 *
 * Deliberately fires on OPEN, not on mention-click, so it works no matter how
 * you got there — bell, Plate row, search, a link a colleague pasted.
 *
 * One-shot query per open (not a subscription): mounting this in a modal must
 * not add a Firestore listener to every detail surface in the app.
 *
 * ```tsx
 * useAutoReadMentions(fault.id, { enabled: isOpen });
 * ```
 */
import { useEffect, useRef } from 'react';
import { markEntityMentionsAsRead } from '@/services/mention.service';
import { getTenantIdSync } from '@/hooks/useTenantId';
import { getSession } from '@/lib/session';

interface Options {
  /** Gate for surfaces that mount before they're visible (modals). */
  enabled?: boolean;
  /** Override the viewer — defaults to the logged-in user. */
  userId?: string;
  tenantId?: string;
}

export function useAutoReadMentions(
  entityId: string | null | undefined,
  { enabled = true, userId, tenantId }: Options = {},
): void {
  // One sweep per entity per mount — re-renders and status changes coming
  // back down the snapshot listener must not re-fire the write.
  const sweptFor = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !entityId) return;
    if (sweptFor.current === entityId) return;

    const viewer = userId ?? getSession().userId ?? '';
    if (!viewer) return;

    sweptFor.current = entityId;

    void markEntityMentionsAsRead(viewer, tenantId ?? getTenantIdSync(), entityId).catch(
      (err) => {
        // Never surface to the user — this is a background courtesy, and the
        // manual "Mark read" control is still there if it fails.
        console.error('[useAutoReadMentions] sweep failed:', err);
        sweptFor.current = null;
      },
    );
  }, [entityId, enabled, userId, tenantId]);
}
